import React, { useEffect, useState } from 'react';
import { useSession } from '../contexts/SessionContext';
import DeepgramKeyVault from './DeepgramKeyVault';
import { TranslationKeysForm } from './TranslationKeysForm';
import { TranslationStatusBar } from './TranslationStatusBar';
import { AutopilotSettings } from './AutopilotSettings';
import { APP_VERSION_LABEL } from '../constants/version';
import { isWellbeingDockEnabled, setWellbeingDockEnabled } from '../utils/wellbeingDock';
import { useTTS } from '../hooks/useTTS';
import {
  COMPONENT_LABELS,
  DEFAULT_COMPONENT_VISIBILITY,
  VISIBILITY_MODES,
  loadComponentVisibility,
  saveComponentVisibility,
} from '../utils/componentVisibility';
import {
  loadLanguagePair,
  saveLanguagePair,
  isEnEsProtectionMode,
  groupedLanguageOptions,
  getLangLabel,
  isOneSocketEnabled,
  setOneSocketEnabled,
} from '../utils/languageConfig';
import { DevSimulatePanel } from './DevSimulatePanel';
import { isDevSimEnabled } from '../utils/devSimulateCaptions';
import { TestHarnessPanel, isTestHarnessEnabled } from './TestHarnessPanel';
import { Phase0SmokeDashboard, isPhase0SmokeEnabled } from './Phase0SmokeDashboard';
import { AuthPanel } from './AuthPanel';
import { CorrectionsBackupPanel } from './CorrectionsBackupPanel';
import { AppBackupPanel } from './AppBackupPanel';
import { CallLogImportPanel } from './CallLogImportPanel';
import { displayDeviceName } from '../utils/audioDeviceLabels';
import { useAuth } from '../contexts/AuthContext';
import { useAudioSource } from '../hooks/useAudioSource';
import { useAudioSettings } from '../contexts/AudioSettingsContext';
import { getDeepgramSettingsPrompt } from '../utils/deepgramSettingsPrompt';
import { getDeepgramKeySource } from '../utils/deepgramRuntimeKey';
import {
  THEME_PALETTES,
  derivePaletteFromImageUrl,
  loadThemePalette,
  parseCssBackgroundUrl,
  saveThemePalette,
} from '../utils/themePalette';
import {
  STT_LATENCY_CHANGED_EVENT,
  STT_LATENCY_MODES,
  loadSttLatencyMode,
  saveSttLatencyMode,
} from '../utils/deepgramListenConfig';
// v4.155.0 / v4.156.0 — the two clinical guards (both OFF by default)
import {
  isDomainRepairEnabled,
  setDomainRepairEnabled,
  isNegationGuardEnabled,
  setNegationGuardEnabled,
  // v4.173.0: the digit-rewrite panic button (ON by default).
  isDigitRewritesEnabled,
  setDigitRewritesEnabled,
} from '../utils/clinicalGuards';
// v4.157.0 — provider biasing. OFF by default; the medical model costs ~2x EN.
import { readSttBias, saveSttBias } from '../utils/deepgramListenConfig';
import { readTurnEndSwitch, saveTurnEndSwitch } from '../utils/turnEnd';
import { readLedgerShadowSwitch, writeLedgerShadowSwitch } from '../utils/sttLedgerShadow';
import {
  buildKeyterms,
  keytermTokenCost,
  keytermsFromCorrections,
  KEYTERM_MAX_TERMS,
} from '../utils/sttKeyterms';
// v4.158.0 — the operator's own ✎ corrections, read to preview what would be sent.
import {
  loadCorrections,
  loadGlossary,
  removeCorrection,
  clearGlossary,
  CORRECTIONS_CHANGED_EVENT,
} from '../utils/transcriptCorrections';
// v4.161.0 — off / exact / phrase: the operator decides how strictly pins apply
import {
  GLOSSARY_MODES,
  GLOSSARY_MODE_LABELS,
  GLOSSARY_MODE_HINTS,
  loadGlossaryMode,
  saveGlossaryMode,
} from '../utils/glossaryMode';
// v4.167.0 — translation latency: FAST by default, operator picks.
import {
  TRANSLATION_LATENCY_MODES,
  TRANSLATION_LATENCY_LABELS,
  TRANSLATION_LATENCY_HINTS,
  loadTranslationLatencyMode,
  saveTranslationLatencyMode,
} from '../utils/translationLatencyMode';
import {
  STT_DIAGNOSTIC_SETTINGS_CHANGED_EVENT,
  readSttDiagnosticSettings,
  setSttDiagnosticSettings,
} from '../utils/sttDiagnosticTrace';
// v4.161.0 — one source of truth for the panel list, labels, search + pins.
import {
  SETTINGS_PANELS,
  groupSettingsPanels,
  searchSettingsPanels,
  loadSettingsPins,
  toggleSettingsPin,
  loadLastSettingsSection,
  saveLastSettingsSection,
  isSettingsPanel,
  getSettingsPanel,
} from '../utils/settingsRegistry';
import {
  INSPECTOR_CHANGED_EVENT,
  readInspectorEnabled,
  setInspectorEnabled,
} from './HudInspector';

const MOODS = ['auto', 'default', 'fast', 'chill'];
const MOOD_LABELS = { auto: 'Trans Auto', default: 'Default', fast: 'Fast', chill: 'Chill' };

export default function SettingsPanel({
  open,
  onClose,
  initialSection = null,
  openReason = null,
  openTrigger = 'general',
}) {
  const {
    translationMood,
    setTranslationMood,
    speechAutoConnect,
    setSpeechAutoConnect,
    // v4.172.0 SHIFT EAR
    shiftEarEnabled,
    setShiftEarEnabled,
    isNotesOpen,
    setIsNotesOpen,
    vaultStatus,
    autoAttachEnabled,
    setAutoAttachEnabled,
    isActive,
    isZombieCall,
  } = useSession();
  // v4.161.0: no explicit target (plain gear) => return to the last panel used.
  const firstSection = () =>
    (isSettingsPanel(initialSection) && initialSection)
    || loadLastSettingsSection()
    || 'deepgram';
  const [section, setSection] = useState(firstSection);
  const [query, setQuery] = useState('');
  const [pins, setPins] = useState(loadSettingsPins);
  const [navNote, setNavNote] = useState('');
  const [personalDock, setPersonalDock] = useState(isWellbeingDockEnabled);
  const [componentVisibility, setComponentVisibility] = useState(loadComponentVisibility);
  const [themePalette, setThemePalette] = useState(loadThemePalette);
  const [themeStatus, setThemeStatus] = useState('');
  const [languagePair, setLanguagePair] = useState(loadLanguagePair);
  const [sttLatencyMode, setSttLatencyMode] = useState(loadSttLatencyMode);
  const [turnEndOn, setTurnEndOn] = useState(readTurnEndSwitch); // v4.197.0
  const [ledgerShadowOn, setLedgerShadowOn] = useState(readLedgerShadowSwitch); // v4.201.0
  // v4.155.0 — domain repair switch (OFF by default; see utils/clinicalGuards.js)
  const [domainRepairOn, setDomainRepairOn] = useState(isDomainRepairEnabled);
  const [oneSocketOn, setOneSocketOn] = useState(isOneSocketEnabled); // v4.177.0
  // v4.156.0 — negation guard switch (read-only warning, its own switch)
  const [negationGuardOn, setNegationGuardOn] = useState(isNegationGuardEnabled);
  // v4.173.0 — digit rewrites ON by default; RAW is the panic button.
  const [digitRewritesOn, setDigitRewritesOn] = useState(isDigitRewritesEnabled);
  // v4.157.0 — provider biasing (the only switches that change what Deepgram bills)
  const [sttBias, setSttBias] = useState(readSttBias);
  // v4.158.0 — which of the operator's own ✎ corrections would become keyterms.
  // Shown on the panel so nothing they typed is ever sent without them seeing it.
  const [userKeytermPreview, setUserKeytermPreview] = useState(() =>
    keytermsFromCorrections(loadCorrections()),
  );
  // v4.161.0 — the pinned glossary and how strictly it applies. The store used to
  // be invisible, which made it impossible to audit what the app was forcing.
  const [glossaryMode, setGlossaryMode] = useState(loadGlossaryMode);
  // v4.167.0 — translation latency ladder. FAST by default: in live interpreting
  // every second is a sentence behind.
  const [latencyMode, setLatencyMode] = useState(loadTranslationLatencyMode);
  const [glossaryEntries, setGlossaryEntries] = useState(() => loadGlossary());
  useEffect(() => {
    const refresh = () => {
      setUserKeytermPreview(keytermsFromCorrections(loadCorrections()));
      setGlossaryEntries(loadGlossary());
    };
    window.addEventListener(CORRECTIONS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(CORRECTIONS_CHANGED_EVENT, refresh);
  }, []);
  const [sttDiagnostics, setSttDiagnostics] = useState(readSttDiagnosticSettings);
  const [inspectorOn, setInspectorOn] = useState(readInspectorEnabled);
  const {
    currentSourceMode,
    switchAudioSourceMode,
    availableInputDevices,
    selectedInputDeviceId,
    refreshInputDevices,
    refreshSelectedDeviceId,
  } = useAudioSource();
  const {
    inputDevices,
    outputDevices,
    selectedSinkId,
    selectedRecMicId,
    changeSinkId,
    changeRecMicId,
    fetchDevices,
  } = useAudioSettings();

  const [disableOnboardingAnimations, setDisableOnboardingAnimations] = useState(() => {
    try {
      return localStorage.getItem('catint_onboarding_anim_disabled_v1') === '1';
    } catch {
      return false;
    }
  });
  const { playTTS } = useTTS();
  const { user: authUser } = useAuth();

  const applyPalette = (palette) => {
    setThemePalette(saveThemePalette(palette));
    setThemeStatus(`${palette.name} palette applied`);
  };

  const derivePaletteFromBackground = async () => {
    setThemeStatus('Reading current background...');
    try {
      const rawUrl = parseCssBackgroundUrl(document.body.style.backgroundImage);
      const url = rawUrl.startsWith('blob:') || rawUrl.startsWith('http')
        ? rawUrl
        : `${window.location.origin}${rawUrl}`;
      const palette = await derivePaletteFromImageUrl(url);
      applyPalette(palette);
    } catch (err) {
      setThemeStatus(err?.message || 'Could not read background');
    }
  };

  useEffect(() => {
    if (open) {
      setSection(firstSection());
      setQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSection]);

  // Remember where the operator was, so the gear never dumps them on Deepgram.
  useEffect(() => {
    if (open) saveLastSettingsSection(section);
  }, [open, section]);

  // v4.167.0: every off-call workspace view (goal wheel, Soundboard Studio,
  // Greeting Editor) is reached from here. Their header buttons are gone, so
  // this is the door — and the place that can say "off-call only" honestly
  // instead of closing the drawer on nothing.
  const WORKSPACE_ACTIONS = {
    'goals-view': { event: 'cat_open_goals_view', label: 'The goal wheel' },
    'soundboard-view': { event: 'cat_open_soundboard_view', label: 'Soundboard Studio' },
    'greeting-editor': { event: 'cat_open_greeting_editor', label: 'The Greeting Editor' },
  };

  const goTo = (id) => {
    const panel = getSettingsPanel(id);
    if (!panel) return;
    const target = panel.action ? WORKSPACE_ACTIONS[panel.action] : null;
    if (target) {
      if (isActive || isZombieCall) {
        setNavNote(`${target.label} is off-call only — stop the call first.`);
        return;
      }
      setNavNote('');
      window.dispatchEvent(new CustomEvent(target.event));
      onClose();
      return;
    }
    if (isSettingsPanel(id)) setSection(id);
  };

  const togglePin = (id) => setPins(toggleSettingsPin(id));

  const searching = query.trim().length > 0;
  const results = searching ? searchSettingsPanels(query) : [];
  // A pinned panel lives in the pinned strip only — never twice on one screen.
  const grouped = groupSettingsPanels(SETTINGS_PANELS.filter((p) => !pins.includes(p.id)));
  const pinnedPanels = SETTINGS_PANELS.filter((p) => pins.includes(p.id));
  // The goal wheel refuses to open during a call, so mark it instead of
  // letting the operator click a button that quietly does nothing.
  const blockedPanels = new Set(
    (isActive || isZombieCall)
      ? SETTINGS_PANELS.filter((p) => p.action).map((p) => p.id)
      : [],
  );

  useEffect(() => {
    const onLatencyChange = (e) => setSttLatencyMode(e.detail || loadSttLatencyMode());
    const onInspectorChange = (e) => setInspectorOn(
      typeof e?.detail?.enabled === 'boolean' ? e.detail.enabled : readInspectorEnabled(),
    );
    const onSttDiagnosticsChange = () => setSttDiagnostics(readSttDiagnosticSettings());
    window.addEventListener(STT_LATENCY_CHANGED_EVENT, onLatencyChange);
    window.addEventListener(INSPECTOR_CHANGED_EVENT, onInspectorChange);
    window.addEventListener(STT_DIAGNOSTIC_SETTINGS_CHANGED_EVENT, onSttDiagnosticsChange);
    return () => {
      window.removeEventListener(STT_LATENCY_CHANGED_EVENT, onLatencyChange);
      window.removeEventListener(INSPECTOR_CHANGED_EVENT, onInspectorChange);
      window.removeEventListener(STT_DIAGNOSTIC_SETTINGS_CHANGED_EVENT, onSttDiagnosticsChange);
    };
  }, []);

  if (!open) return null;

  const deepgramPrompt = section === 'deepgram'
    ? getDeepgramSettingsPrompt(openReason, openTrigger)
    : null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999998,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 'min(380px, 96vw)',
          height: '100%',
          background: '#0b1220',
          borderLeft: '1px solid rgba(255,255,255,0.08)',
          padding: 14,
          overflowY: 'auto',
          color: '#fff',
          fontFamily: 'monospace',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>Settings [{APP_VERSION_LABEL}]</h3>
          <button type="button" onClick={onClose} style={tabBtn}>✕</button>
        </div>

        <div className="settings-search">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search settings…"
            aria-label="Search settings"
            className="settings-search-input"
          />
          {query && (
            <button type="button" className="btn" onClick={() => setQuery('')}>✕</button>
          )}
        </div>

        {navNote && <div className="settings-nav-note" role="status">{navNote}</div>}

        {searching ? (
          <div className="settings-nav" role="listbox" aria-label="Settings results">
            {results.length === 0 && (
              <p className="settings-nav-empty">
                Nothing matches “{query}”. Try: call log · minutes · goal · key · theme · language.
              </p>
            )}
            {results.map((p) => (
              <SettingsNavButton
                key={p.id}
                panel={p}
                active={section === p.id}
                pinned={pins.includes(p.id)}
                onSelect={goTo}
                onTogglePin={togglePin}
                accountSignedIn={!!authUser}
                blocked={blockedPanels.has(p.id)}
              />
            ))}
          </div>
        ) : (
          <div className="settings-nav">
            {pinnedPanels.length > 0 && (
              <div className="settings-nav-group">
                <div className="settings-nav-group-label">★ Pinned</div>
                {pinnedPanels.map((p) => (
                  <SettingsNavButton
                    key={`pin-${p.id}`}
                    panel={p}
                    active={section === p.id}
                    pinned
                    onSelect={goTo}
                    onTogglePin={togglePin}
                    accountSignedIn={!!authUser}
                    blocked={blockedPanels.has(p.id)}
                  />
                ))}
              </div>
            )}
            {grouped.map((g) => (
              <div className="settings-nav-group" key={g.id}>
                <div className="settings-nav-group-label">{g.label}</div>
                {g.panels.map((p) => (
                  <SettingsNavButton
                    key={p.id}
                    panel={p}
                    active={section === p.id}
                    pinned={pins.includes(p.id)}
                    onSelect={goTo}
                    onTogglePin={togglePin}
                    accountSignedIn={!!authUser}
                    blocked={blockedPanels.has(p.id)}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {vaultStatus === 'unlocking' && (
          <p style={{ color: '#f59e0b', fontSize: 11, marginTop: 8 }}>⏳ Decrypting key…</p>
        )}

        {section === 'today' && (
          <div style={{ marginTop: 12 }}>
            <div className="settings-section-head">Call log — correct today&apos;s minutes</div>
            <CallLogImportPanel />
          </div>
        )}

        {section === 'account' && (
          <div style={{ marginTop: 12 }}>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', margin: '0 0 10px', lineHeight: 1.45 }}>
              v1 cloud sync: UI prefs + soundboard text only. No transcripts, stats, or API keys.
            </p>
            <AuthPanel />
          </div>
        )}

{section === 'deepgram' && (
          <div style={{ marginTop: 12 }}>
            {deepgramPrompt && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  fontSize: 11,
                  lineHeight: 1.45,
                  background: deepgramPrompt.tone === 'info'
                    ? 'rgba(59,130,246,0.12)'
                    : 'rgba(245,158,11,0.12)',
                  border: `1px solid ${
                    deepgramPrompt.tone === 'info'
                      ? 'rgba(59,130,246,0.35)'
                      : 'rgba(245,158,11,0.4)'
                  }`,
                  color: deepgramPrompt.tone === 'info' ? '#bfdbfe' : '#fcd34d',
                }}
              >
                <strong style={{ display: 'block', marginBottom: 4 }}>{deepgramPrompt.title}</strong>
                {deepgramPrompt.body}
              </div>
            )}
            <DeepgramKeyVault embedded />
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>STT latency mode</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {Object.values(STT_LATENCY_MODES).map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => saveSttLatencyMode(mode.id)}
                    style={{
                      ...tabBtn,
                      background: sttLatencyMode === mode.id ? 'rgba(34, 211, 238, 0.22)' : tabBtn.background,
                    }}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
                FAST = 100ms chunks + endpointing 150 (reconnects live audio). BAL = steadier 250ms / 300ms.
              </p>
            </div>
            {/* v4.177.0 — one EN+ES socket (fixes repeated text). Next CONNECT. */}
            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                id="settings-one-socket"
                aria-pressed={oneSocketOn}
                onClick={() => setOneSocketOn(setOneSocketEnabled(!oneSocketOn))}
                style={{
                  ...tabBtn,
                  background: oneSocketOn ? 'rgba(34, 211, 238, 0.22)' : tabBtn.background,
                }}
              >
                One socket EN+ES: {oneSocketOn ? 'ON' : 'OFF'}
              </button>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
                ON = one mixed-language stream: no repeated sentences, half the Deepgram minutes. OFF = old EN + ES pair. Applies on next CONNECT.
              </p>
            </div>
            {/* v4.155.0 / v4.156.0 — the two clinical guards. OFF = today's behaviour. */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>Clinical guards</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  aria-pressed={domainRepairOn}
                  onClick={() => setDomainRepairOn(setDomainRepairEnabled(!domainRepairOn))}
                  style={{
                    ...tabBtn,
                    background: domainRepairOn ? 'rgba(34, 211, 238, 0.22)' : tabBtn.background,
                  }}
                >
                  Term repair: {domainRepairOn ? 'ON' : 'OFF'}
                </button>
                <button
                  type="button"
                  aria-pressed={negationGuardOn}
                  onClick={() => setNegationGuardOn(setNegationGuardEnabled(!negationGuardOn))}
                  style={{
                    ...tabBtn,
                    background: negationGuardOn ? 'rgba(245, 158, 11, 0.22)' : tabBtn.background,
                  }}
                >
                  Negation guard: {negationGuardOn ? 'ON' : 'OFF'}
                </button>
              </div>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
                <strong>Term repair</strong> fixes known medical/legal words Deepgram hears as plain English —
                “all but a roll” → albuterol, “exit bit” → exhibit. It never touches a number, and it never
                invents a missing “denies”. <strong>Negation guard</strong> only puts a ⚠ on a line whose
                negation looks dropped (read-only, it changes no words). Both ship OFF on purpose: turn one on
                and listen to a call before you trust it.
              </p>
            </div>
            {/* v4.173.0 — the digit-rewrite panic button. ON = full pipeline. */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>Numbers</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  id="settings-digit-rewrites"
                  aria-pressed={digitRewritesOn}
                  onClick={() => setDigitRewritesOn(setDigitRewritesEnabled(!digitRewritesOn))}
                  style={{
                    ...tabBtn,
                    background: digitRewritesOn
                      ? 'rgba(34, 211, 238, 0.22)'
                      : 'rgba(239, 68, 68, 0.22)',
                  }}
                >
                  Format numbers: {digitRewritesOn ? 'ON' : 'RAW'}
                </button>
              </div>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
                <strong>ON</strong> groups phone numbers, folds spoken numbers (“one fifty” → 150) and turns
                “at 3 30” into 3:30. <strong>RAW</strong> shows exactly what Deepgram said — nothing is
                rewritten, so nothing can be guessed wrong. Numbers stay highlighted and click-to-copy in both
                modes. If a call is quoting you prices and a number looks wrong, tap RAW.
              </p>
            </div>
            {/* v4.157.0 — provider biasing. The only switches here that change the bill. */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>Provider biasing</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  aria-pressed={sttBias.medicalModel}
                  onClick={() =>
                    setSttBias(saveSttBias({ ...sttBias, medicalModel: !sttBias.medicalModel }))
                  }
                  style={{
                    ...tabBtn,
                    background: sttBias.medicalModel ? 'rgba(239, 68, 68, 0.22)' : tabBtn.background,
                  }}
                >
                  Medical model (EN): {sttBias.medicalModel ? 'ON' : 'OFF'}
                </button>
                <button
                  type="button"
                  aria-pressed={sttBias.keyterm}
                  onClick={() => setSttBias(saveSttBias({ ...sttBias, keyterm: !sttBias.keyterm }))}
                  style={{
                    ...tabBtn,
                    background: sttBias.keyterm ? 'rgba(34, 211, 238, 0.22)' : tabBtn.background,
                  }}
                >
                  Keyterm bias: {sttBias.keyterm ? 'ON' : 'OFF'}
                </button>
                {/* v4.158.0: the only switch that would send something the user TYPED. */}
                <button
                  type="button"
                  aria-pressed={sttBias.userKeyterms}
                  onClick={() =>
                    setSttBias(saveSttBias({ ...sttBias, userKeyterms: !sttBias.userKeyterms }))
                  }
                  style={{
                    ...tabBtn,
                    background: sttBias.userKeyterms ? 'rgba(34, 211, 238, 0.22)' : tabBtn.background,
                  }}
                >
                  My corrections as terms: {sttBias.userKeyterms ? 'ON' : 'OFF'}
                </button>
              </div>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
                <strong>Medical model</strong> sends the English socket to <code>nova-3-medical</code> — roughly
                2x the EN price. <strong>Keyterm bias</strong> sends {buildKeyterms('en').length}/
                {KEYTERM_MAX_TERMS} terms ({keytermTokenCost(buildKeyterms('en'))} tokens), built only from
                words we have already seen mangled, medical first. <strong>My corrections as terms</strong> adds
                the words you fixed with ✎ ({userKeytermPreview.length} ready) — that is the one setting that
                would send something you typed, so it stays off until you turn it on. Both apply on the next
                CONNECT. Off by default: run one call and compare before keeping any on.
              </p>
              {userKeytermPreview.length > 0 && (
                <p style={{ fontSize: 10, color: '#93c5fd', margin: '4px 0 0' }}>
                  From your ✎ corrections: {userKeytermPreview.join(', ')}
                </p>
              )}
            </div>
            {/* v4.197.0 — turn end by audio silence instead of wall clock (utils/turnEnd.js). */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>Bubble split</div>
              <button
                type="button"
                aria-pressed={turnEndOn}
                onClick={() => setTurnEndOn(saveTurnEndSwitch(!turnEndOn))}
                style={{
                  ...tabBtn,
                  background: turnEndOn ? 'rgba(34, 211, 238, 0.22)' : tabBtn.background,
                }}
              >
                Split on real silence: {turnEndOn ? 'ON' : 'OFF'}
              </button>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
                <strong>OFF</strong> (today): a new bubble starts after 2.5 s with no text — a Deepgram delay
                mid-sentence can cut one sentence in two. <strong>ON</strong>: a new bubble starts only after
                1 s of real silence in the audio (Deepgram <code>UtteranceEnd</code>), or 8 s with no text.
                Same price. Applies on the next CONNECT. Try one call ON, one OFF.
              </p>
            </div>
            {/* v4.201.0 — Caption Engine v2 Shadow Mode (utils/sttLedgerShadow.js). */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>Caption Engine v2 (Shadow)</div>
              <button
                type="button"
                id="settings-ledger-shadow"
                aria-pressed={ledgerShadowOn}
                onClick={() => setLedgerShadowOn(writeLedgerShadowSwitch(!ledgerShadowOn))}
                style={{
                  ...tabBtn,
                  background: ledgerShadowOn ? 'rgba(34, 211, 238, 0.22)' : tabBtn.background,
                }}
              >
                Shadow Engine v2: {ledgerShadowOn ? 'ON' : 'OFF'}
              </button>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
                <strong>OFF</strong> (default): v1 engine only. <strong>ON</strong>: runs Engine v2 (ledger) in
                the background with zero UI impact, measuring word loss between v1 and v2 on live calls. Check
                console <code>__CAT_LEDGER_SHADOW()</code> anytime.
              </p>
            </div>
          </div>
        )}
        {section === 'dg-key' && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 8 }}>
              DG Key
            </div>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 12 }}>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
                Runtime Deepgram API key status
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>
                  Source: {getDeepgramKeySource()}
                </span>
              </div>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
                Use the DeepgramKeyVault tab to manage encrypted key storage.
              </p>
            </div>
          </div>
        )}
        {section === 'language' && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', margin: 0, lineHeight: 1.45 }}>
              Left column = patient / column 1. Right column = interpreter / column 2. Default EN↔ES.
            </p>
            <label style={{ fontSize: 11, color: '#93c5fd' }}>
              Left column (patient)
              <select
                value={languagePair.left}
                onChange={(e) => {
                  const next = saveLanguagePair({ ...languagePair, left: e.target.value });
                  setLanguagePair(next);
                }}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 6, background: '#0f172a', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6 }}
              >
                {Object.entries(groupedLanguageOptions()).map(([group, items]) => (
                  <optgroup key={group} label={group}>
                    {items.map((item) => (
                      <option key={item.code} value={item.code}>{item.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 11, color: '#6ee7b7' }}>
              Right column (interpreter)
              <select
                value={languagePair.right}
                onChange={(e) => {
                  const next = saveLanguagePair({ ...languagePair, right: e.target.value });
                  setLanguagePair(next);
                }}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 6, background: '#0f172a', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6 }}
              >
                {Object.entries(groupedLanguageOptions()).map(([group, items]) => (
                  <optgroup key={group} label={group}>
                    {items.map((item) => (
                      <option key={item.code} value={item.code}>{item.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <p style={{ fontSize: 10, color: isEnEsProtectionMode(languagePair) ? 'rgba(16,185,129,0.85)' : '#fbbf24', margin: 0 }}>
              {isEnEsProtectionMode(languagePair)
                ? `Active pair: ${getLangLabel(languagePair.left)} ↔ ${getLangLabel(languagePair.right)} — US number/phone protections ON.`
                : `Active pair: ${getLangLabel(languagePair.left)} ↔ ${getLangLabel(languagePair.right)} — US number/phone protections paused (EN↔ES only).`}
            </p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', margin: 0 }}>
              Space / Alt+Space force left/right STT lane for 30s. Changes reconnect live audio if attached.
            </p>
          </div>
        )}

        {section === 'translation' && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <TranslationStatusBar />
            <div>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>Translation mode</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {MOODS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setTranslationMood(m)}
                    style={{
                      ...tabBtn,
                      background: translationMood === m ? 'rgba(16,185,129,0.25)' : tabBtn.background,
                    }}
                  >
                    {MOOD_LABELS[m] || m}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
                Trans Auto = translate sealed sentences only (saves API quota).
              </p>
            </div>
            <TranslationKeysForm />
            {/* v4.167.0 — how long to wait on a translation engine before moving on */}
            <div>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>
                Translation latency
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {TRANSLATION_LATENCY_MODES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={latencyMode === m}
                    onClick={() => setLatencyMode(saveTranslationLatencyMode(m))}
                    style={{
                      ...tabBtn,
                      background: latencyMode === m ? 'rgba(16,185,129,0.25)' : tabBtn.background,
                    }}
                  >
                    {TRANSLATION_LATENCY_LABELS[m]}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
                {TRANSLATION_LATENCY_HINTS[latencyMode]} Applies to the next line, no reload. Lower
                = faster, but a slow engine gets abandoned sooner.
              </p>
            </div>
            {/* v4.161.0 — the glossary: what is pinned, and how strictly it applies */}
            <div>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>Pinned glossary</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {GLOSSARY_MODES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={glossaryMode === m}
                    onClick={() => setGlossaryMode(saveGlossaryMode(m))}
                    style={{
                      ...tabBtn,
                      background: glossaryMode === m ? 'rgba(16,185,129,0.25)' : tabBtn.background,
                    }}
                  >
                    {GLOSSARY_MODE_LABELS[m]}
                  </button>
                ))}
                {glossaryEntries.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      clearGlossary();
                      setGlossaryEntries(loadGlossary());
                    }}
                    style={{ ...tabBtn }}
                    title="Delete every pinned translation for this language pair"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
                {GLOSSARY_MODE_HINTS[glossaryMode]} Fix a translation with ✎ to pin it.
                {glossaryMode === 'phrase'
                  ? ' PHRASE mode applies a pinned term inside every later sentence — best for CSA/legal, where the same term returns in different wording.'
                  : ''}
              </p>
              {glossaryEntries.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {glossaryEntries.map((e) => (
                    <div
                      key={e.key}
                      style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                        fontSize: 10,
                        color: 'rgba(255,255,255,0.75)',
                        background: 'rgba(255,255,255,0.04)',
                        borderRadius: 4,
                        padding: '3px 6px',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.sourceHeard} → {e.corrected}
                      </span>
                      <button
                        type="button"
                        aria-label={`Delete pinned translation ${e.sourceHeard}`}
                        onClick={() => {
                          removeCorrection(e.key);
                          setGlossaryEntries(loadGlossary());
                        }}
                        style={{ ...tabBtn, padding: '0 6px' }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {section === 'behavior' && (
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={speechAutoConnect}
                onChange={(e) => setSpeechAutoConnect(e.target.checked)}
              />
              Speech Auto Connect — start call when speech detected
            </label>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>
              v4.92.0: between calls the app keeps its ears open (idle ear — Deepgram gets NO audio, only pings, so no usage) and starts the call by itself when speech appears. Requires audio attached (one CONNECT press per browser session).
            </p>
            <label
              id="settings-shift-ear"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginTop: 14 }}
            >
              <input
                type="checkbox"
                checked={shiftEarEnabled}
                onChange={(e) => setShiftEarEnabled(e.target.checked)}
              />
              Shift Ear — keep one Deepgram socket warm all shift
            </label>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>
              v4.172.0: an off-call socket death used to drop the app to DISCONNECTED and deaf
              (the VAD went with it), so the next call needed a CONNECT press. Now the ear
              quietly re-opens the pair, rotates it before Deepgram drops it, and lets it go
              after an hour of silence. Nothing is sent while warm, so it costs $0 and banks
              no minutes. Off = exactly the old behaviour.
            </p>
            <AutopilotSettings />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginTop: 14 }}>
              <input
                type="checkbox"
                checked={personalDock}
                onChange={(e) => {
                  setWellbeingDockEnabled(e.target.checked);
                  setPersonalDock(e.target.checked);
                }}
              />
              Show interpreter wellbeing tools (desk breaks, hydration, pauses)
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginTop: 14 }}>
              <input
                type="checkbox"
                checked={disableOnboardingAnimations}
                onChange={(e) => {
                  const next = e.target.checked;
                  setDisableOnboardingAnimations(next);
                  try {
                    localStorage.setItem('catint_onboarding_anim_disabled_v1', next ? '1' : '0');
                  } catch {}
                }}
              />
              Disable onboarding animations (guide spotlight only)
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginTop: 14 }}>
              <input
                type="checkbox"
                checked={autoAttachEnabled}
                onChange={(e) => setAutoAttachEnabled(e.target.checked)}
              />
              Auto-attach interpreting tab at/after 09:00 (Chrome)
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginTop: 14 }}>
              <input
                type="checkbox"
                checked={!!isNotesOpen}
                onChange={(e) => setIsNotesOpen(e.target.checked)}
              />
              📝 Session notes sidebar (same toggle as the bottom-dock button)
            </label>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '6px 0 0' }}>
              OFF by default to avoid blocking browser permission dialogs on first use.
              Tab share needs Chrome or Edge — Cursor Simple Browser cannot pick tabs; use Mic test mode (🎤) there, or open the app in Chrome.
            </p>

            {process.env.NODE_ENV !== 'production' && (
              <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>Dev UI</div>
                <button
                  type="button"
                  style={{ ...tabBtn, fontSize: 11, marginRight: 8 }}
                  onClick={() => {
                    try {
                      window.dispatchEvent(
                        new CustomEvent('cat_demo_scenario', { detail: 'ui_call' })
                      );
                    } catch (_) {}
                  }}
                  title="Start the call UI (no real audio capture). Dev only."
                >
                  UI In Call Mode (no audio)
                </button>
                <button
                  type="button"
                  style={{ ...tabBtn, fontSize: 11 }}
                  onClick={() => {
                    try {
                      window.dispatchEvent(
                        new CustomEvent('cat_demo_scenario', { detail: 'reset' })
                      );
                    } catch (_) {}
                  }}
                  title="Reset the demo call UI."
                >
                  Reset
                </button>
              </div>
            )}
            {isDevSimEnabled() && <DevSimulatePanel />}
            {isTestHarnessEnabled() && <TestHarnessPanel />}
            {isPhase0SmokeEnabled() && <Phase0SmokeDashboard />}
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>TTS route test</div>
              <button
                type="button"
                style={{ ...tabBtn, fontSize: 11 }}
                onClick={() => playTTS('Prueba de audio para la llamada.', 'es')}
              >
                Test TTS route (ES sample)
              </button>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>
                Plays through local speakers + virtual output (same path as bubble play). Inworld prefetch hooks here later.
              </p>
            </div>
          </div>
        )}

        {section === 'data' && (
          <div style={{ marginTop: 12 }}>
            <div className="settings-section-head">App backup — goals · month progress · greetings · settings</div>
            <AppBackupPanel />
            <div className="settings-section-head" style={{ marginTop: 14 }}>Taught corrections</div>
            <CorrectionsBackupPanel />
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 14, lineHeight: 1.45 }}>
              Compliance notes: <code>docs/compliance/operational-notes.md</code>
            </p>
          </div>
        )}

        {section === 'layout' && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.45 }}>
            Off-call layout: <strong style={{ color: '#93c5fd' }}>dashboard-header</strong> (scoreboard + progress) on top,
            {' '}<strong style={{ color: '#93c5fd' }}>interpret pane</strong> below (~80% viewport).
          </div>
        )}

        {section === 'display' && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6 }}>
              Theme palette [{APP_VERSION_LABEL}]
            </div>
            <div className="theme-palette-panel">
              <div className="theme-palette-row">
                {Object.values(THEME_PALETTES).map((palette) => (
                  <button
                    key={palette.id}
                    type="button"
                    className={`theme-swatch-btn${themePalette.id === palette.id ? ' is-active' : ''}`}
                    onClick={() => applyPalette(palette)}
                    title={`${palette.name} palette`}
                    aria-pressed={themePalette.id === palette.id}
                  >
                    <span className="theme-swatch-dot" style={{ background: palette.accent }} />
                    {palette.name}
                  </button>
                ))}
                <button
                  type="button"
                  className={`theme-swatch-btn${themePalette.id === 'derived' ? ' is-active' : ''}`}
                  onClick={derivePaletteFromBackground}
                  title="Sample current background image and build a readable palette"
                  aria-pressed={themePalette.id === 'derived'}
                >
                  <span className="theme-swatch-dot" style={{ background: themePalette.accent }} />
                  From bg
                </button>
              </div>
              <div className="theme-palette-preview" style={{ borderColor: themePalette.border, background: themePalette.panel }}>
                <span style={{ color: themePalette.accent }}>Accent</span>
                <span style={{ color: themePalette.accentHover || themePalette.accent }}>Hover</span>
                <span>Panel</span>
              </div>
              {themeStatus && <div className="theme-palette-status">{themeStatus}</div>}
            </div>

            <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 4 }}>
              Component visibility [{APP_VERSION_LABEL}]
            </div>
            <label
              className="hud-inspector-exempt"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 10,
                color: '#93c5fd',
                cursor: 'pointer',
                marginBottom: 10,
              }}
            >
              <input
                type="checkbox"
                checked={inspectorOn}
                onChange={(e) => {
                  setInspectorOn(e.target.checked);
                  setInspectorEnabled(e.target.checked);
                }}
                style={{ margin: 0 }}
              />
              HUD inspector ⌖ (debug: hover + click = copy selector, Alt+I)
            </label>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '0 0 10px' }}>
              Progress bars = monthly + daily timelines. Changes save instantly.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: 10, background: 'rgba(239,68,68,0.05)' }}>
                <div style={{ fontSize: 11, fontWeight: 900, color: '#fca5a5', marginBottom: 6 }}>
                  Reset UI state (v{APP_VERSION_LABEL})
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.45, marginBottom: 8 }}>
                  Clears persisted UI/layout flags only (prevents zombie-call / wrong off-call workspace on reload). Does not clear Deepgram keys or transcripts.
                </div>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      if (typeof window.catintResetUiState === 'function') window.catintResetUiState();
                    } catch {}
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid rgba(239,68,68,0.45)',
                    background: 'rgba(239,68,68,0.15)',
                    color: '#fca5a5',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 900,
                  }}
                  title="Clears UI-only localStorage flags and reloads the app"
                >
                  Reset UI state
                </button>
              </div>

              {Object.keys(DEFAULT_COMPONENT_VISIBILITY).map((id) => (
                <div key={id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                    {COMPONENT_LABELS[id] || id}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {VISIBILITY_MODES.map((mode) => (
                      <label
                        key={mode}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          fontSize: 10,
                          cursor: 'pointer',
                          padding: '2px 6px',
                          borderRadius: 4,
                          background: componentVisibility[id] === mode ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.04)',
                          border: componentVisibility[id] === mode ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(255,255,255,0.08)',
                        }}
                      >
                        <input
                          type="radio"
                          name={`vis-${id}`}
                          checked={componentVisibility[id] === mode}
                          onChange={() => {
                            const next = saveComponentVisibility({ ...componentVisibility, [id]: mode });
                            setComponentVisibility(next);
                          }}
                          style={{ margin: 0 }}
                        />
                        {mode === 'always' ? 'Always' : mode === 'on_call' ? 'On call' : mode === 'off_call' ? 'Off call' : 'Hidden'}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {section === 'audio' && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', margin: 0, lineHeight: 1.45 }}>
              Choose how CatIntAssist captures <strong>interpreter audio</strong> for STT.
              Default is Tab share to preserve your current working setup.
            </p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', margin: 0, lineHeight: 1.45 }}>
              Mic mode (🎤) also forces TTS and soundboard to <strong>local speakers only</strong> — for phone assistant / debug.
            </p>

            <div style={{ padding: 9, border: '1px solid rgba(148,163,184,.28)', borderRadius: 8, background: 'rgba(15,23,42,.7)' }}>
              <div style={{ fontSize: 11, color: '#c4b5fd', fontWeight: 700 }}>Admin STT diagnostics</div>
              <p style={{ ...devHintStyle, marginTop: 3 }}>
                Ctrl+Alt+D opens the hidden inspector. Traces and audio stay in memory only, active-call only, and are wiped on STOP.
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
                <button
                  type="button"
                  aria-pressed={sttDiagnostics.traceEnabled}
                  onClick={() => {
                    const next = { ...readSttDiagnosticSettings(), traceEnabled: !sttDiagnostics.traceEnabled };
                    setSttDiagnostics(setSttDiagnosticSettings(next));
                  }}
                  style={{ ...tabBtn, background: sttDiagnostics.traceEnabled ? 'rgba(139,92,246,.3)' : tabBtn.background }}
                >
                  Trace: {sttDiagnostics.traceEnabled ? 'ON' : 'OFF'}
                </button>
                <button
                  type="button"
                  aria-pressed={sttDiagnostics.audioRingEnabled}
                  onClick={() => {
                    const next = { ...readSttDiagnosticSettings(), audioRingEnabled: !sttDiagnostics.audioRingEnabled };
                    setSttDiagnostics(setSttDiagnosticSettings(next));
                  }}
                  style={{ ...tabBtn, background: sttDiagnostics.audioRingEnabled ? 'rgba(239,68,68,.3)' : tabBtn.background }}
                >
                  Last 60s audio: {sttDiagnostics.audioRingEnabled ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 6, fontWeight: 700 }}>
                Audio source (STT) — moved here from the header
              </div>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '0 0 6px', lineHeight: 1.45 }}>
                v4.165.0: the TAB / VB buttons left the header strip to free up width. This
                panel is now the only place to change where call audio is captured. Reachable
                mid-call through the ⚙ in the same header row.
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('cat_set_mic_mode', { detail: { on: false } }));
                    switchAudioSourceMode('tab');
                  }}
                  style={{
                    ...tabBtn,
                    background: currentSourceMode === 'tab' ? 'rgba(59,130,246,0.25)' : tabBtn.background,
                    borderColor: currentSourceMode === 'tab' ? 'rgba(59,130,246,0.55)' : 'rgba(255,255,255,0.10)',
                  }}
                  aria-pressed={currentSourceMode === 'tab'}
                >
                  Tab share
                </button>
                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('cat_set_mic_mode', { detail: { on: false } }));
                    switchAudioSourceMode('virtualCable');
                  }}
                  style={{
                    ...tabBtn,
                    background: currentSourceMode === 'virtualCable' ? 'rgba(245,158,11,0.20)' : tabBtn.background,
                    borderColor: currentSourceMode === 'virtualCable' ? 'rgba(245,158,11,0.55)' : 'rgba(255,255,255,0.10)',
                  }}
                  aria-pressed={currentSourceMode === 'virtualCable'}
                >
                  Virtual cable
                </button>
                {/* v4.139.0: mic mode moved here from the retired header pill (M hotkey still toggles) */}
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('cat_set_mic_mode', { detail: { on: true } }))}
                  style={{ ...tabBtn, borderColor: 'rgba(255,255,255,0.10)' }}
                  title="Mic STT — device microphone (phone / no tab share). Soundboard plays local only. Toggle off with the M key."
                >
                  Mic 🎤
                </button>
              </div>
            </div>

            {/* v4.103.0: Sound settings — 3 device selectors (mic shown here were
                previously only in the I/O strip, which stays compact/hidden). */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 11, color: '#93c5fd', fontWeight: 700 }}>
                Devices
              </div>

              {/* v4.185.0: "Call transcription input (mic)" + "Client mic — MIC VERIFY"
                  rows removed: the first was mislabeled (it was the live mic sent to
                  the call), the second only a status readout. 🎤 mic-mode pick stays
                  in the I/O strip. Rows left: recording mic, greeting output, STT source. */}

              <label style={{ fontSize: 11, color: '#6ee7b7' }}>
                🎙️ Greeting recording mic
                <select
                  value={selectedRecMicId}
                  onChange={(e) => changeRecMicId(e.target.value)}
                  onFocus={() => fetchDevices({ requestMicPermissionForLabels: true })}
                  style={devSelectStyle}
                >
                  <option value="">Same as 🎤 mic (I/O strip)</option>
                  {inputDevices.map((d, i) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Mic ${i + 1}`}
                    </option>
                  ))}
                </select>
                <span style={{ ...devHintStyle }}>
                  Mic used only to record soundboard greetings — pick a different one (e.g. Realtek) — recording only, never sent to the call.
                </span>
              </label>

              <label style={{ fontSize: 11, color: '#6ee7b7' }}>
                🔊 Greeting playback output
                <select
                  value={selectedSinkId}
                  onChange={(e) => changeSinkId(e.target.value)}
                  onFocus={() => fetchDevices({ requestMicPermissionForLabels: false })}
                  style={devSelectStyle}
                >
                  <option value="">Default (system speakers)</option>
                  {outputDevices.map((d, i) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {displayDeviceName(d, i, 'out', null, outputDevices)}
                    </option>
                  ))}
                </select>
                <span style={{ ...devHintStyle }}>
                  For Voicemeeter, choose Voicemeeter Input (no number). In 1–5 are optional extension inputs. The call site's microphone must use Voicemeeter Output / Out B1. Speakers are for local testing.
                </span>
              </label>
            </div>

            {currentSourceMode === 'virtualCable' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ fontSize: 11, color: '#6ee7b7' }}>
                  Virtual audio input (STT source)
                  <select
                    value={selectedInputDeviceId}
                    onChange={(e) => refreshSelectedDeviceId(e.target.value)}
                    onFocus={() => refreshInputDevices()}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 4,
                      padding: 6,
                      background: '#0f172a',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 6,
                      maxHeight: 130,
                      overflowY: 'auto',
                    }}
                  >
                    <option value="">Default (let browser pick)</option>
                    {availableInputDevices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Input ${d.deviceId.slice(0, 5)}`}
                      </option>
                    ))}
                  </select>
                </label>
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.50)', margin: 0, lineHeight: 1.45 }}>
                  Choose the recording device carrying incoming call audio. With greetings on Voicemeeter and call audio on VB-Cable, use <strong>CABLE Output</strong>. Out B1 carries your outgoing mic/greetings on that route.
                  If device labels are blank, CatIntAssist will request temporary audio permission.
                </p>
                <button
                  type="button"
                  style={{ ...tabBtn, fontSize: 11, marginTop: 2, width: 'fit-content' }}
                  onClick={refreshInputDevices}
                >
                  Refresh devices
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const tabBtn = {
  padding: '4px 8px',
  fontSize: 10,
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  color: '#fff',
  cursor: 'pointer',
};

// v4.103.0: shared styles for the Devices selectors in the Audio tab.
const devSelectStyle = {
  display: 'block',
  width: '100%',
  marginTop: 4,
  padding: 6,
  background: '#0f172a',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 6,
  maxHeight: 130,
  overflowY: 'auto',
};

const devHintStyle = {
  display: 'block',
  fontSize: 10,
  color: 'rgba(255,255,255,0.50)',
  marginTop: 3,
  lineHeight: 1.45,
};

/**
 * v4.161.0 — one row in the settings nav: the panel name, what it is for, and
 * the pin toggle. Split out because the same row is rendered in the pinned
 * strip, inside a group, and in the search results.
 */
function SettingsNavButton({ panel, active, pinned, onSelect, onTogglePin, accountSignedIn, blocked }) {
  const label = panel.id === 'account' && accountSignedIn ? 'Account ✓' : panel.label;
  return (
    <div className={`settings-nav-row${active ? ' is-active' : ''}${blocked ? ' is-blocked' : ''}`}>
      <button
        type="button"
        className="settings-nav-btn"
        aria-current={active ? 'true' : undefined}
        onClick={() => onSelect(panel.id)}
      >
        <span className="settings-nav-name">
          {label}
          {blocked && <span className="settings-nav-flag">off-call only</span>}
        </span>
        <span className="settings-nav-hint">{panel.hint}</span>
      </button>
      <button
        type="button"
        className="settings-nav-pin"
        aria-pressed={pinned}
        aria-label={`${pinned ? 'Unpin' : 'Pin'} ${label}`}
        title={pinned ? `Unpin ${label}` : `Pin ${label} to the top`}
        onClick={() => onTogglePin(panel.id)}
      >
        {pinned ? '★' : '☆'}
      </button>
    </div>
  );
}
